import queryString from 'query-string';
import { getDomain, getH5Domain, getKrnDomain } from '@/common/api';
import { openUrl } from '@/common/bridge/openUrl';
import { getEnv } from '@/common/api';
import { isIOS, localStorage } from '@kibt/krn-utils';
import yoda from '@kibt/bridge';
import { versionCompare } from '@kibt/web-utils/version';
import { appVersion } from '../device-info';
import { modifyUrlWithParams } from '../utils/modifyUrlWithParams';
import { useExtParamsObject } from '@/page/detail/views/content/utils';
import { SHELF_QUERY_KEY } from '@/utils/shelfSource';
import { getQuery, getQuerys, SCENE } from '../const';

export function useJumpToGoodsDetailPage() {
    const extParamsObj = useExtParamsObject();

    const jumpToGoodsDetailPage = (params: {
        itemId: string | number;
        sellerUserId?: string | number;
        merchantId?: string | number;
        pageType?: string | number;
        sellerSource?: string | number;
        extParams?: Record<string, any>;
        scene?: string;
        // 短视频凑单
        fromVideoAddon?: number;
        // 是否打开半屏
        halfWebview?: boolean;
        retrievalInfo?: Record<string, any>;
        // 后端下发跳转链接
        backEndDispatchUrl?: string;
        //
        source?: string;
        // shelfSource 加货架的参数
        [SHELF_QUERY_KEY]?: string;
        // ab下钻使用
        abInfo?: {
            groupName?: string;
            experimentName?: string;
            experimentValue?: string;
        };
    }) => {
        const {
            itemId,
            sellerUserId = '',
            merchantId = '',
            pageType = '',
            sellerSource = '',
            extParams,
            scene = '',
            fromVideoAddon,
            halfWebview = false,
            retrievalInfo,
            backEndDispatchUrl,
            source,
            [SHELF_QUERY_KEY]: shelfQuery,
            abInfo = {},
        } = params;
        const finalExtParams = Object.assign({}, extParamsObj, extParams);
        // 如果是达人分销商品，会带达人ID信息，需要透传  sellerUserId / merchantId
        const env = getEnv();
        // const krnUrl =
        //     `bundleId=Detail&componentName=Detail&env=${env}&itemId=${itemId}&scene=${scene}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&retrievalInfo=${retrievalInfoEncoded}` +
        //     (pageType ? `&pageType=${pageType}` : '') +
        //     (sellerSource ? `&sellerSource=${sellerSource}` : '');
        const krnUrl = queryString.stringify(
            {
                bundleId: 'Detail',
                componentName: 'Detail',
                env,
                itemId,
                scene,
                sellerUserId,
                merchantId,
                extParams: JSON.stringify(finalExtParams),
                fromVideoAddon,
                retrievalInfo: JSON.stringify(retrievalInfo),
                pageType,
                sellerSource,
                themeStyle: 1,
                source,
            },
            { sort: false, skipEmptyString: true }
        );
        const query = {
            itemId,
            scene,
            env,
            retrievalInfo: JSON.stringify(retrievalInfo),
            sellerUserId,
            merchantId,
            extParams: JSON.stringify(finalExtParams),
            fromVideoAddon,
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
            pageType,
            sellerSource,
            source,
            // url追加ab信息，用于ab实验下钻分析
            ...(abInfo || {}),
        };
        if (shelfQuery) {
            query[SHELF_QUERY_KEY] = shelfQuery;
        }
        const url = queryString.stringifyUrl(
            {
                url: `${getKrnDomain()}/krn-web/detail`,
                query,
            },
            { skipEmptyString: true, skipNull: true }
        );
        const backEndUrl = backEndDispatchUrl ? modifyUrlWithParams(backEndDispatchUrl, { ...query }, ['itemId', 'merchantId', 'redirectUrl']) : '';
        return openUrl({ url, halfWebview }, { url: backEndUrl });
    };

    return { jumpToGoodsDetailPage };
}

export function useGetGoodsDetailPageUrl() {
    const extParamsObj = useExtParamsObject();
    const getGoodsDetailPageUrl = ({
        itemId,
        sellerUserId = '',
        merchantId = '',
        extParams,
        scene = '',
        fromVideoAddon,
        retrievalInfo,
    }: {
        itemId: string | number;
        sellerUserId?: string | number;
        merchantId?: string | number;
        extParams?: Record<string, any>;
        scene?: string;
        // 短视频凑单
        fromVideoAddon?: number;
        // 是否打开半屏
        halfWebview?: boolean;
        retrievalInfo?: Record<string, any>;
    }) => {
        const finalExtParams = Object.assign({}, extParamsObj, extParams);
        const extParamsEncoded = `${encodeURIComponent(JSON.stringify(finalExtParams))}`;
        const retrievalInfoEncoded = `${encodeURIComponent(JSON.stringify(retrievalInfo))}`;
        // 如果是达人分销商品，会带达人ID信息，需要透传  sellerUserId / merchantId
        const env = getEnv();
        const krnUrl = `bundleId=Detail&componentName=Detail&env=${env}&itemId=${itemId}&scene=${scene}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&retrievalInfo=${retrievalInfoEncoded}`;
        const url = `${getKrnDomain()}/krn-web/detail?itemId=${itemId}&scene=${scene}&retrievalInfo=${retrievalInfoEncoded}&sellerUserId=${sellerUserId}&merchantId=${merchantId}&env=${env}&extParams=${extParamsEncoded}&fromVideoAddon=${fromVideoAddon}&__launch_options__=${encodeURIComponent(
            JSON.stringify({
                topBarPosition: 'fixed',
            })
        )}&redirectUrl=${encodeURIComponent(krnUrl)}`;
        return url;
    };
    return { getGoodsDetailPageUrl };
}

/**
 * @deprecated
 * 这里没有传营销的相关信息
 */
export function useJumpToConfirmOrder() {
    const scene = getQuery('scene');
    const extParamsObj = useExtParamsObject();

    // 跳转到确认下单页面
    // feat-url-dispatch 购物车跳提单
    const jumpToConfirmOrder = async (props: {
        fromMyShoppingCar?: number; // 注意：最开始只有购物车使用这个字段表示需要从localStorage获取购买信息，后来多个页面都使用这个字段来标识需要从localStorage获取购买信息，不仅仅是从购物车场景到提单页，故此字段没有具体的链路含义，仅仅表示需要从localStorage获取商品信息。
        addressId?: string | number;
        productItems?: {
            itemId: string | number;
            merchantId: string | number;
            skuId: string | number;
            sellerMerchantId?: string | number;
            num: number;
        }[];
        extParams?: {
            cId?: number;
            cT?: number;
            cS?: string;
        };
        backendDispatchUrl?: string;
        scene?: string;
    }) => {
        const { addressId, productItems, fromMyShoppingCar, backendDispatchUrl, scene: sceneFromPraram } = props;
        const extParams = Object.assign({}, extParamsObj, props?.extParams);
        const env = getEnv();
        if (fromMyShoppingCar) {
            await localStorage.setItem(
                'MyShoppingCarParams',
                JSON.stringify({
                    productItems,
                })
            );
        }
        const krnUrl = queryString.stringify(
            {
                bundleId: 'EcommOrderConfirm',
                componentName: 'OrderConfirm',
                env,
                fromMyShoppingCar,
                // extParams: extParamsEncoded,
                extParams: JSON.stringify(extParams),
            },
            { sort: false }
        );
        const query = {
            scene: sceneFromPraram || scene || SCENE.DEFAULT_SCENE,
            referer_page: 'shopcar',
            addressId: addressId || '',
            fromMyShoppingCar,
            env,
            extParams: JSON.stringify(extParams),
            __launch_options__: JSON.stringify({
                enableLoading: false,
                topBarPosition: 'fixed',
            }),
            hyId: 'kibt-fe-shop-c-krn',
            redirectUrl: krnUrl,
        };
        const url = queryString.stringifyUrl(
            {
                url: `${getKrnDomain()}/krn-web/order-confirm`,
                query,
            },
            { skipEmptyString: true, skipNull: true }
        );
        console.log('jumpToConfirmOrder url::', url);
        const jumpUrl = backendDispatchUrl ? modifyUrlWithParams(backendDispatchUrl, { ...query }, ['merchantId', 'redirectUrl']) : '';

        return openUrl({ url, refererPage: 'MyShoppingCar' }, { url: jumpUrl });
    };

    return { jumpToConfirmOrder };
}

export function jumpToConfirmOrderWhenAddressAddDone(params: Record<string, any>) {
    const scene = getQuery('scene');
    const krnUrl = queryString.stringify(
        {
            bundleId: 'EcommOrderConfirm',
            componentName: 'OrderConfirm',
        },
        { sort: false }
    );
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/order-confirm`,
        query: {
            env: getEnv(),
            krnFollowParams: 1,
            redirectUrl: krnUrl,
            scene,
            ...(params || {}),
        },
    });
    return openUrl({ url, refererPage: 'AddressAddDone', routerReplace: true });
}

// 跳到店铺页面
// feat-url-dispatch 商详跳店铺
// feat-url-dispatch 小黄车跳跳店铺
// feat-url-dispatch 购物车跳店铺页
export const jumpToMerchant = async (params: {
    merchantId: string | number;
    referItemIds?: string; // 上级页面枚举id
    isToBDetail?: boolean;
    extraInfoType?: string;
    type?: string;
    isPromotion?: string;
    authorId?: string;
    backendDispatchUrl?: string;
    shelfSourceString?: string; // 形如 item_detail..1231313231
}) => {
    const path = '/shop/c/merchant?';
    const { merchantId, isToBDetail, extraInfoType, type, isPromotion, referItemIds, authorId, backendDispatchUrl, shelfSourceString } = params;
    const env = getEnv();
    const jumpUrl = queryString.stringifyUrl(
        {
            url: `${getDomain()}/shop/c/merchant`,
            query: {
                pageType: isToBDetail ? 'seller' : '',
                isPromotion: isPromotion,
                merchantId,
                env,
                krnFollowParams: 1,
                __launch_options__: JSON.stringify({
                    enableLoading: false,
                    topBarPosition: 'fixed',
                }),
                extra_info: JSON.stringify({
                    entrance_type_v2: type || '',
                    entrance_type: extraInfoType || '',
                }),
                authorId,
                referItemIds,
                redirectUrl: queryString.stringify({
                    bundleId: 'EcommMerchant',
                    componentName: 'Merchant',
                }),
                [SHELF_QUERY_KEY]: shelfSourceString,
            },
        },
        { skipEmptyString: true, skipNull: true }
    );
    const backEndUrl = backendDispatchUrl
        ? modifyUrlWithParams(backendDispatchUrl, { ...queryString.parseUrl(jumpUrl).query }, ['itemId', 'merchantId', 'redirectUrl'])
        : '';
    openUrl({ url: jumpUrl }, { url: backEndUrl });
};

const H5Base = '__launch_options__=%7B%22topBarPosition%22%3A%22fixed%22%7D&hyId=kibt-fe-shop-c-v1';

export function jumpToAddress({ addressId, entertSource, pageorigin }: { addressId: string | number; entertSource: number; pageorigin: string }) {
    const url = `${getDomain()}/shop/c/address?pageorigin=${pageorigin ?? ''}&addressIdInQuery=${addressId}&entertSource=${
        entertSource ?? 2
    }&formKrn=1&${H5Base}`;
    openUrl({ url });
}

// 跳转到新增地址
export const jumpToAddAddress = (props: {
    id?: string | number;
    type?: 'update';
    enterSource: string | number;
    pageorigin: string;
    enterAddressId?: string | number;
    isDefault: boolean;
}) => {
    const baseUrl = `${getDomain()}/shop/c/address/add?${H5Base}`;
    const url = queryString.stringifyUrl({
        url: baseUrl,
        query: {
            pageorigin: props?.pageorigin ?? '',
            enterSource: props?.enterSource ?? 2,
            formKrn: '1',
            type: props.type,
            id: props.id,
            enterAddressId: props?.enterAddressId,
            ...(props.isDefault ? { isDefault: 'TRUE' } : {}),
        },
    });
    console.log('jumpToAddAddress::', url);
    openUrl({ url });
};

// 商城TAB上线后，直接跳转TAB路由，其中url为无TAB兜底打开的H5
export const jumpToKwaiShop = ({ source }: { source: string }) => {
    // openUrl 中会强制修改source，这里加一个oSource代替原本的source
    const url = `${getDomain()}/shop/c/kwai-shop-new?isPrerenderPage=true&source=${source}&oSource=${source}&${H5Base}`;
    const res = versionCompare(appVersion, '9.11.30');
    if (res >= 0) {
        const schema = `ikwai://home/ecommerce?url=${encodeURIComponent(url)}`;
        (yoda.kwaiInvoke ?? yoda.invoke)?.('component.loadUri', {
            url: schema,
        });
    } else {
        openUrl({ url });
    }
    console.log('jumpToKwaiShop');
};

// 打开非 tab 的 shop 页
export const jumpToProfileKwaiShop = ({ source }: { source: string }) => {
    const url = `${getDomain()}/shop/c/kwai-shop-new?isPrerenderPage=true&source=${source}&oSource=${source}&${H5Base}`;
    openUrl({ url });
};

export const jumpToProfileKwaiShopByPush = ({ source, itemId }: { source: string; itemId: string }) => {
    const url = `${getDomain()}/shop/c/kwai-shop-new?isPrerenderPage=true&source=${source}&oSource=${source}&feedItemId=${itemId}&${H5Base}`;
    openUrl({ url });
};

export const jumpToCodStatusPage = (receiptId: string | number) => {
    const url = `${getDomain()}/shop/c/pay-center/cod-status?receiptId=${receiptId}&${H5Base}`;
    openUrl({ url });
};

interface RefoundApplicationParams {
    type: string;
    orderId: string;
    mode?: string;
    subOrderId?: string;
    aftersaleQuantity?: number;
}
export const jumpToRefundApplication = (p: RefoundApplicationParams) => {
    const { type, orderId, mode, ...rest } = p;
    const url = queryString.stringifyUrl({
        url: `${getDomain()}/shop/c/aftersale/refund-application`,
        query: {
            ...rest,
            aftersaleType: type,
            orderId,
            mode,
            hyId: 'kibt-fe-shop-c-v1',
            __launch_options__: '%7B%22topBarPosition%22%3A%22fixed%22%7D',
        },
    });
    return openUrl({
        url,
    });
};

function getKsPayWallerDomain() {
    const runtimeEnv = getEnv();
    if (runtimeEnv === 'prod') return 'https://www.kwai-pay.com';
    if (runtimeEnv === 'prt') return 'https://kwai-pay-prt.test.gifshow.com';
    if (runtimeEnv === 'staging') return 'https://kspay-oversea-staging.test.gifshow.com';
    return 'https://www.kwai-pay.com';
}

// 跳转中台钱包页
export function openWalletPage(account_group_key = 'IDN_REFUND_ROLLBACK') {
    const url = `${getKsPayWallerDomain()}/page/kspay-oversea-wallet-withdraw-h5/v3/wallet?account_group_key=${account_group_key}`;
    return openUrl({ url });
}

// 跳转到订单详情
export const jumpToOrderDetail = ({ orderId, orderListTabIndex = '0' }) => {
    const krnUrl = queryString.stringify({
        bundleId: 'OrderDetail',
        componentName: 'OrderDetail',
        env: getEnv(),
        // orderId,
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/order-detail`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            // fromBundle: 'EcommOrderDetail',
            orderId,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
            orderListTabIndex,
        },
    });
    return openUrl({ url });
};

// 跳转新增评论页
export const jumpToAddComment = ({ orderId, halfWebview = false, commentSource = '', productStars = 0, subOrderId = 0 }) => {
    let krnUrl = queryString.stringify({
        bundleId: 'EcommAddComment',
        componentName: 'EcommAddComment',
        env: getEnv(),
    });
    if (halfWebview) {
        krnUrl = queryString.stringify({
            bundleId: 'EcommAddComment',
            componentName: 'EcommAddComment',
            env: getEnv(),
            height: 1,
            foldRatio: isIOS ? 0.79 : 0.75, // ios打开半屏不可拖动全屏的时候，0.75高度不够，根因：ios不同容器的高度计算逻辑不一致
            openSlideFullScreen: 0, // iosrn 新增跳转参数 表明是可拖拽的半屏
            isHalf: 1, // iosrn 新增跳转参数
        });
    }
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/add-comment`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            fromBundle: 'EcommOrderDetail',
            initialRouteName: 'CommentAdd',
            orderId,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
            productStars,
            subOrderId,
            halfScene: halfWebview ? 'half' : 'full',
            commentSource,
        },
    });
    if (halfWebview) {
        return openUrl({
            url,
            halfWebview: true,
            fullScreen: '0',
            options: {
                hideMask: false,
                maskColor: '#00000055',
            },
        });
    }
    return openUrl({ url });
};

// 跳转评论页
export const jumpToComment = (payload: { itemId: string | number; scene?: string }) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommComment',
        componentName: 'EcommComment',
        env: getEnv(),
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/comment`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            ...payload,
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};

// 跳转视频评论双列
export const jumpToBuyerGallery = (payload: {
    extParams: {
        cT: string;
        cId: string;
        cS: string;
    };
    sellerMerchantId: string | number;
    sellerUserId: string | number;
    itemId: string | number;
    scene?: string;
    showGalleryType?: string;
}) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommAddComment',
        componentName: 'EcommAddComment',
        env: getEnv(),
    });

    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/add-comment`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            ...payload,
            extParams: JSON.stringify(payload.extParams),
            extParamsForPreReq: JSON.stringify({
                buyParams: {
                    sellerMerchantId: String(payload.sellerMerchantId ?? ''),
                    sellerUserId: String(payload?.sellerUserId ?? ''),
                    scene: String(payload?.scene ?? ''),
                    cT: payload?.extParams?.cT ?? '',
                    cS: payload?.extParams?.cS ?? '',
                    cId: payload?.extParams?.cId ?? '',
                },
            }),
            initialRouteName: 'CommentGallery',
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};

export const jumpToCouponMakeup = (
    urlParams: {
        couponId?: number;
        batchId?: number;
        itemId?: number;
        merchantId?: number;
        taskId?: number;
        userTaskId?: number;
        backTo?: 'kwaiShop';
        // 凑单页来源，用于埋点
        from?: 'homeCouponPopup' | 'homeTaskCoupon' | 'payResultPopup' | 'payResultTaskCoupon' | 'recommendGoodsTaskCoupon';
    } = {}
) => {
    const url = queryString.stringifyUrl(
        {
            url: `${getDomain()}/shop/c/coupon/make-up`,
            query: {
                ...urlParams,
                hyId: 'kibt-fe-shop-c-v1',
                __launch_options__: '%7B%22topBarPosition%22%3A%22fixed%22%7D',
            },
        },
        { skipEmptyString: true, skipNull: true }
    );

    openUrl({
        url,
    });
};

// 打开搜索结果页
export const jumpToSearch = () => {
    const { liveId, authorId, live_source, extParams, operation_source } = getQuerys();
    const krnUrl = queryString.stringify({
        bundleId: 'EcomSearch',
        componentName: 'EcomSearch',
        env: getEnv(),
        height: 1,
        foldRatio: isIOS ? 0.79 : 0.75, // ios打开半屏不可拖动全屏的时候，0.75高度不够，根因：ios不同容器的高度计算逻辑不一致
        openSlideFullScreen: 0, // iosrn 新增跳转参数 表明是可拖拽的半屏
        isHalf: 1, // iosrn 新增跳转参数
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/search`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            liveId,
            authorId,
            liveSource: live_source,
            operationSource: operation_source,
            redirectUrl: krnUrl,
            extParams,
        },
    });
    return openUrl({ url, halfWebview: true, fullScreen: '0' });
};

/**
 *  跳转新版KRN商城搜索页
 * @param payload
 */
export const jumpToShopSearch = (
    payload: { product?: string; tipType?: string; keyword?: string; itemId?: string; categoryId?: string; oSource?: string } = {}
) => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcommShopSearch',
        componentName: 'Search',
        env: getEnv(),
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/shop/c/search`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            ...payload,
            hyId: 'kibt-fe-shop-c-v1',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url });
};
// 打开优惠券弹窗
export const jumpToCouponDialog = () => {
    const krnUrl = queryString.stringify({
        bundleId: 'EcomCouponDialog',
        componentName: 'EcomCouponDialog',
        env: getEnv(),
        height: 1,
        foldRatio: 0.99,
        openSlideFullScreen: 0,
        isHalf: 1, // iosrn 新增跳转参数
    });
    const url = queryString.stringifyUrl({
        url: `${getKrnDomain()}/krn-web/coupon-dialog`,
        query: {
            krnFollowParams: 1,
            env: getEnv(),
            hyId: 'kibt-fe-shop-c-krn',
            __launch_options__: JSON.stringify({
                topBarPosition: 'fixed',
            }),
            redirectUrl: krnUrl,
        },
    });
    return openUrl({ url, halfWebview: true, fullScreen: '0', height: 0.99 });
};

// 打开h5搜索页
export const jumpToSearchPage = (query: Record<string, any> = {}) => {
    const url = queryString.stringifyUrl(
        {
            url: `${getDomain()}/shop/c/search?${H5Base}`,
            query,
        },
        { skipEmptyString: true, skipNull: true }
    );

    console.log('jumpToSearchPage', url);

    openUrl({
        url,
    });
};

export const jumpToLivingRoom = (userId: number, source = 'kwaiShop', requestType = '31', backEndDispatchUrl?: string) => {
    const deepLink = `ikwai://live/play/${userId}?requestType=${requestType}&operation_source=${source}`;
    const res = versionCompare(appVersion, '9.11.30');
    if (res >= 0) {
        (yoda.kwaiInvoke ?? yoda.invoke)?.('component.loadUri', {
            url: backEndDispatchUrl || deepLink,
        });
    }
};

const getCallUrl = () => {
    const path = window.location.pathname;
    const query = queryString.parse(window.location.search);

    const krnObj = (() => {
        if (path.endsWith('/detail')) {
            return {
                bundleId: 'Detail',
                componentName: 'Detail',
                lastPath: 'detail.html',
            };
        }
        if (path.includes('/order-detail')) {
            return {
                bundleId: 'OrderDetail',
                componentName: 'OrderDetail',
                lastPath: 'order-detail.html',
            };
        }
        if (path.includes('/address')) {
            return {
                bundleId: 'EcommAddress',
                componentName: 'EcommAddress',
                lastPath: 'address.html',
            };
        }
        if (path.includes('/order-confirm')) {
            return {
                bundleId: 'EcommOrderConfirm',
                componentName: 'OrderConfirm',
                lastPath: 'order-confirm.html',
            };
        }
        return null;
    })();
    // KRN 的 URL
    if (krnObj && krnObj.bundleId) {
        const krnUrl = queryString.stringify(
            {
                bundleId: krnObj.bundleId,
                componentName: krnObj.componentName,
                themeStyle: 1,
            },
            { sort: false }
        );
        const url = queryString.stringifyUrl(
            {
                url: `${getKrnDomain()}/krn-web/${krnObj.lastPath}`,
                query: {
                    redirectUrl: krnUrl,
                    ...query,
                    hyId: 'kibt-fe-shop-c-krn',
                    __launch_options__: JSON.stringify({
                        topBarPosition: 'fixed',
                    }),
                },
            },
            { skipEmptyString: true, skipNull: true }
        );
        return url;
    }

    // H5 的 URL
    const h5Path = (() => {
        if (path.endsWith('order-list')) {
            return '/shop/c/pay-center/order-list';
        }
        return null;
    })();

    if (h5Path) {
        const url = queryString.stringifyUrl(
            {
                url: getH5Domain() + h5Path,
                query: {
                    ...query,
                    hyId: 'kibt-fe-shop-c-v1',
                    __launch_options__: JSON.stringify({
                        topBarPosition: 'fixed',
                    }),
                },
            },
            { skipEmptyString: true, skipNull: true }
        );
        return url;
    }
    return null;
};

// 外投通用唤起功能，根据当前 url 做映射
export const openInApp = () => {
    const path = window.location.pathname;
    const callUrl = getCallUrl();

    if (!callUrl) return null;

    const pageId = `ecomm_commodity_${path}`;
    const share_id = `openInApp_${Math.random() * 1000}`;
    // 生成onelink链接，拼接所需要的参数
    const targetUrl = queryString.stringifyUrl(
        {
            url: callUrl,
            query: {
                shareInfo: null,
                innerPage: null,
                share_id,
                share_item_type: 'goods',
            },
        },
        { skipNull: true, sort: false }
    );
    const onLinkQuery = {
        pid: pageId,
        is_retargeting: true,
        af_force_deeplink: true,
        land_info: JSON.stringify({
            linkParams: {
                primary_directory: 'SHARE',
                secondary_directory: 'ecomm',
                link_id: `${pageId}_${Date.now()}`,
                item_type: 'h5_item_type',
                item_info: pageId,
            },
        }),
        deep_link_sub1: `pid=${pageId}`,
        af_dp: `ikwai://webview?url=${encodeURIComponent(targetUrl)}`,
        ug_referrer: `pid=${pageId}`,
        deep_link_value: `ikwai://webview?url=${encodeURIComponent(targetUrl)}`,
        // 为onelink追加的参数，用来回流链路追踪
        af_sub1: JSON.stringify({
            share_id,
            shareInfo: {
                target_url: queryString.stringifyUrl(
                    {
                        // url: window.location.href,
                        url: callUrl,
                        query: {
                            shareInfo: null,
                            innerPage: null,
                            share_id,
                            share_item_type: 'goods',
                        },
                    },
                    { skipNull: true, sort: false }
                ),
            },
        }),
    };
    // onelink工具生成的onelink地址，然后再把参数替换成动态的，https://applinksplatform.corp.kuaishou.com/
    const oneLink = queryString.stringifyUrl({ url: 'https://go.onelink.me/iGO2', query: onLinkQuery });
    window.open(oneLink);
};
